import React, { useEffect, useState } from "react";
import achievementApi from "../../services/achievementApi";
import targetApi from "../../services/targetApi";
import DataTable from "../../components/common/DataTable";
import { useToast } from "../../components/common/Toast";
import { FaEdit, FaTrash, FaDownload } from "react-icons/fa";
import "../../styles/target.css";
import "../../styles/adminGlobal.css";

const STATUSES = ["Approved", "Pending", "Rejected"];

/* =========================================================
   Admin: Target Achievements
   View / search / filter / sort / export / edit / delete
   every achievement submitted across the company.
   Endpoints:
     GET    /api/achievements
     GET    /api/achievements/export (CSV)
     PUT    /api/achievements/:id
     DELETE /api/achievements/:id
========================================================= */
function TargetAchievementsAdmin() {
  const toast = useToast();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const limit = 10;

  const [search, setSearch] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("a.achievement_date");
  const [sortDir, setSortDir] = useState("DESC");

  const [users, setUsers] = useState([]);

  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    targetApi.getTargetableUsers().then((res) => setUsers(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, employeeId, status, dateFrom, dateTo, sortBy, sortDir]);

  const currentFilters = () => ({
    search: search || undefined,
    employee_id: employeeId || undefined,
    status: status || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    sort_by: sortBy,
    sort_dir: sortDir,
    page,
    limit,
  });

  const fetchList = () => {
    setLoading(true);
    achievementApi
      .getAllAchievements(currentFilters())
      .then((res) => {
        setRows(res.data.data || []);
        setTotal(res.data.total || 0);
      })
      .catch(() => toast.error("Failed to load achievements"))
      .finally(() => setLoading(false));
  };

  const handleSortChange = (col) => {
    const map = {
      employee_name: "u.name",
      completed_quantity: "a.completed_quantity",
      achievement_date: "a.achievement_date",
      status: "a.status",
    };
    const dbCol = map[col] || col;
    if (sortBy === dbCol) {
      setSortDir((d) => (d === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortBy(dbCol);
      setSortDir("DESC");
    }
  };

  const handleExport = () => {
    achievementApi
      .exportAchievements(currentFilters())
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "target_achievements.csv");
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(() => toast.error("Failed to export achievements"));
  };

  const openEdit = (row) => {
    setEditing({
      id: row.id,
      completed_quantity: row.completed_quantity,
      achievement_date: row.achievement_date ? row.achievement_date.slice(0, 10) : "",
      remarks: row.remarks || "",
      status: row.status,
    });
  };

  const handleEditChange = (e) => setEditing((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSaveEdit = (e) => {
    e.preventDefault();
    setSaving(true);
    achievementApi
      .updateAchievement(editing.id, {
        completed_quantity: Number(editing.completed_quantity),
        achievement_date: editing.achievement_date,
        remarks: editing.remarks,
        status: editing.status,
      })
      .then(() => {
        toast.success("Achievement updated");
        setEditing(null);
        fetchList();
      })
      .catch((err) => toast.error(err?.response?.data?.message || "Failed to update"))
      .finally(() => setSaving(false));
  };

  const handleDelete = (row) => {
    if (!window.confirm(`Delete achievement #${row.id}?`)) return;
    achievementApi
      .deleteAchievement(row.id)
      .then(() => {
        toast.success("Achievement deleted");
        fetchList();
      })
      .catch(() => toast.error("Failed to delete achievement"));
  };

  const columns = [
    { key: "employee_name", label: "Employee", sortable: true },
    { key: "target_title", label: "Target", sortable: false },
    { key: "completed_quantity", label: "Completed", sortable: true },
    { key: "remaining_quantity", label: "Remaining", sortable: false },
    {
      key: "achievement_date",
      label: "Date",
      sortable: true,
      render: (r) => new Date(r.achievement_date).toLocaleDateString("en-IN"),
    },
    { key: "remarks", label: "Remarks", render: (r) => r.remarks || "-" },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (r) => (
        <span className={`status-pill status-${(r.status || "").toLowerCase()}`}>{r.status}</span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (r) => (
        <div className="row-actions">
          <button className="icon-btn" onClick={() => openEdit(r)} title="Edit">
            <FaEdit />
          </button>
          <button className="icon-btn danger" onClick={() => handleDelete(r)} title="Delete">
            <FaTrash />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="target-achievements-admin-page">
      <div className="mytarget-header">
        <div>
          <h2>Target Achievements</h2>
          <p>Review, edit and export every achievement submitted by your team</p>
        </div>
        <button className="btn-secondary" onClick={handleExport}>
          <FaDownload /> Export CSV
        </button>
      </div>

      <div className="filters-bar">
        <input
          placeholder="Search employee, target or remarks..."
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />
        <select
          value={employeeId}
          onChange={(e) => {
            setPage(1);
            setEmployeeId(e.target.value);
          }}
        >
          <option value="">All employees</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value);
          }}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => {
            setPage(1);
            setDateFrom(e.target.value);
          }}
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => {
            setPage(1);
            setDateTo(e.target.value);
          }}
        />
      </div>

      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        emptyIcon="🏆"
        emptyText="No achievements found"
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={handleSortChange}
        page={page}
        limit={limit}
        total={total}
        onPageChange={setPage}
      />

      {editing && (
        <div className="modal-overlay" onClick={() => setEditing(null)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Achievement #{editing.id}</h3>
            <form onSubmit={handleSaveEdit}>
              <div className="form-row">
                <label>Completed Quantity</label>
                <input
                  type="number"
                  min="0"
                  name="completed_quantity"
                  value={editing.completed_quantity}
                  onChange={handleEditChange}
                />
              </div>
              <div className="form-row">
                <label>Achievement Date</label>
                <input
                  type="date"
                  name="achievement_date"
                  value={editing.achievement_date}
                  onChange={handleEditChange}
                />
              </div>
              <div className="form-row">
                <label>Remarks</label>
                <textarea name="remarks" rows={3} value={editing.remarks} onChange={handleEditChange} />
              </div>
              <div className="form-row">
                <label>Status</label>
                <select name="status" value={editing.status} onChange={handleEditChange}>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TargetAchievementsAdmin;
